import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, Space, Table, message } from "antd";
import PageTitle from "../../components/PageTitle";
import {
  createViewingBlacklist,
  deleteViewingBlacklist,
  getAllHouses,
  getViewingBlacklist,
} from "../../api";

const ViewingBlacklist = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [houseId, setHouseId] = useState(undefined);
  const [houses, setHouses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [unbanRow, setUnbanRow] = useState(null);
  const [unbanLoading, setUnbanLoading] = useState(false);
  const [form] = Form.useForm();

  const loadHouses = useCallback(async () => {
    try {
      const res = await getAllHouses();
      const list = Array.isArray(res?.data) ? res.data : res?.data?.list || res?.list || [];
      setHouses(Array.isArray(list) ? list : []);
    } catch {
      setHouses([]);
    }
  }, []);

  const load = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const res = await getViewingBlacklist({
        page: p,
        page_size: ps,
        keyword: keyword || undefined,
        house_id: houseId || undefined,
      });
      if (res?.success) {
        setData(res.data?.list || []);
        setTotal(Number(res.data?.total || 0));
      } else {
        message.error(res?.error || "加载失败");
      }
    } catch {
      message.error("加载看房黑名单失败");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword, houseId]);

  useEffect(() => {
    loadHouses();
  }, [loadHouses]);

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseId]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      const res = await createViewingBlacklist({
        house_id: values.house_id,
        phone: String(values.phone || "").trim(),
        remark: values.remark || "",
      });
      if (!res?.success) throw new Error(res?.error || "拉黑失败");
      message.success("已拉黑");
      setModalOpen(false);
      form.resetFields();
      load(page, pageSize);
    } catch (e) {
      message.error(e?.message || "拉黑失败");
    }
  };

  const handleUnbanOk = async () => {
    if (!unbanRow?.id) return;
    setUnbanLoading(true);
    try {
      const res = await deleteViewingBlacklist(unbanRow.id);
      if (res?.success) {
        message.success("已解封");
        setUnbanRow(null);
        load(page, pageSize);
      } else {
        message.error(res?.error || "解封失败");
      }
    } catch (e) {
      message.error(e?.response?.data?.error || e?.message || "解封失败");
    } finally {
      setUnbanLoading(false);
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 80 },
    {
      title: "房源",
      dataIndex: "house_name",
      render: (v, r) => v || `房源#${r.house_id}`,
    },
    { title: "手机号", dataIndex: "phone", width: 140 },
    { title: "备注", dataIndex: "remark", ellipsis: true },
    {
      title: "时间",
      dataIndex: "created_at",
      width: 180,
      render: (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—"),
    },
    {
      title: "操作",
      width: 100,
      render: (_, row) => (
        <Button type="link" danger size="small" onClick={() => setUnbanRow(row)}>
          解封
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="看房黑名单" />
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          showSearch
          placeholder="筛选房源"
          style={{ width: 260 }}
          value={houseId}
          onChange={setHouseId}
          optionFilterProp="label"
          options={houses.map((h) => ({
            value: h.id,
            label: h.name || h.house_name || `房源#${h.id}`,
          }))}
        />
        <Input.Search
          allowClear
          placeholder="手机号/备注"
          style={{ width: 200 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={() => {
            setPage(1);
            load(1, pageSize);
          }}
        />
        <Button
          type="primary"
          onClick={() => {
            form.resetFields();
            setModalOpen(true);
          }}
        >
          拉黑
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
      />
      <Modal
        title="拉黑手机号"
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        zIndex={2100}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="house_id" label="房源" rules={[{ required: true, message: "请选择房源" }]}>
            <Select
              showSearch
              placeholder="选择房源"
              optionFilterProp="label"
              options={houses.map((h) => ({
                value: h.id,
                label: h.name || h.house_name || `房源#${h.id}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: "请输入手机号" },
              { pattern: /^\d{11}$/, message: "请输入11位手机号" },
            ]}
          >
            <Input placeholder="11位手机号" maxLength={11} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={unbanRow ? `解封手机号 ${unbanRow.phone}？` : "解封"}
        open={!!unbanRow}
        onOk={handleUnbanOk}
        onCancel={() => !unbanLoading && setUnbanRow(null)}
        confirmLoading={unbanLoading}
        okText="解封"
        okButtonProps={{ danger: true }}
        zIndex={2200}
        destroyOnClose
      >
        <div>房源：{unbanRow?.house_name || unbanRow?.house_id || "—"}</div>
      </Modal>
    </div>
  );
};

export default ViewingBlacklist;
